import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Checkbox, 
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
  Radio,
  RadioGroup
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import axios from 'axios';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

// Validation schema
const GrievanceSchema = Yup.object().shape({
  anonymous: Yup.boolean(),
  full_name: Yup.string().when('anonymous', {
    is: false,
    then: () => Yup.string().required('Full name is required'),
    otherwise: () => Yup.string().notRequired()
  }),
  employee_id: Yup.string().when('anonymous', {
    is: false,
    then: () => Yup.string().required('Employee ID is required'),
    otherwise: () => Yup.string().notRequired()
  }),
  department: Yup.string().when('anonymous', {
    is: false,
    then: () => Yup.string().required('Department is required'),
    otherwise: () => Yup.string().notRequired()
  }),
  designation: Yup.string().when('anonymous', {
    is: false,
    then: () => Yup.string().required('Designation is required'),
    otherwise: () => Yup.string().notRequired()
  }),
  contact_number: Yup.string().when('anonymous', {
    is: false,
    then: () => Yup.string().required('Contact number is required'),
    otherwise: () => Yup.string().notRequired()
  }),
  email: Yup.string().when('anonymous', {
    is: false,
    then: () => Yup.string().email('Invalid email').required('Email is required'),
    otherwise: () => Yup.string().email('Invalid email').notRequired()
  }),
  nature_of_grievance: Yup.string().required('Nature of grievance is required'),
  grievance_other_detail: Yup.string(),
  date_of_incident: Yup.date().required('Date of incident is required'),
  reporting_date: Yup.date().required('Reporting date is required'),
  location_of_incident: Yup.string().required('Location of incident is required'),
  individuals_involved: Yup.string(),
  grievance_description: Yup.string().required('Grievance description is required'),
  steps_taken: Yup.string(),
  preferred_resolution: Yup.string().required('Preferred resolution is required'),
  declaration: Yup.boolean().oneOf([true], 'You must accept the declaration')
});

const GrievanceFormTab = () => {
  const { user, getAuthHeaders } = useAuth();
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('form'); // 'form' or 'list'

  // Initial form values
  const initialValues = {
    anonymous: false,
    full_name: user?.full_name || '',
    employee_id: user?.employee_id || '',
    department: user?.department || '',
    designation: user?.jobTitle || '',
    contact_number: user?.contact_number || '',
    email: user?.email || '',
    nature_of_grievance: '',
    grievance_other_detail: '',
    date_of_incident: '',
    reporting_date: '',
    location_of_incident: '',
    individuals_involved: '',
    grievance_description: '',
    steps_taken: '',
    preferred_resolution: '',
    declaration: false
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!user?.id) {
      toast.error('User information not available. Please refresh the page.');
      setSubmitting(false);
      return;
    }
    
    try {
      console.log('Submitting grievance for user:', user);
      console.log('Form values:', values);
      
      const response = await axios.post(
        `${API_URL}/existing/grievance`,
        values,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Grievance submitted successfully');
      resetForm();
      fetchGrievances(); // Refresh the list
      setViewMode('list');
    } catch (error) {
      console.error('Error submitting grievance:', error);
      toast.error(error.response?.data?.error || 'Failed to submit grievance');
    } finally {
      setSubmitting(false);
    }
  };

  // useFormik hook
  const formik = useFormik({
    initialValues,
    validationSchema: GrievanceSchema,
    onSubmit: handleSubmit,
  });

  // Fetch user's grievances
  const fetchGrievances = async () => {
    if (!user?.id) {
      console.warn('User ID not available, skipping grievance fetch');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/existing/grievance/user/${user.id}`,
        { headers: getAuthHeaders() }
      );
      setGrievances(response.data);
    } catch (error) {
      console.error('Error fetching grievances:', error);
      if (error.response?.status === 404) {
        setGrievances([]);
      } else {
        toast.error('Failed to fetch grievances');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('User object in GrievanceFormTab:', user);
    if (user?.id) {
      fetchGrievances();
    }
  }, [user]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved': return 'success';
      case 'Pending': return 'warning';
      case 'Escalated': return 'error';
      default: return 'default';
    }
  };

  const formatDateLocal = (dateString) => {
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
  <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          Grievance Management
        </Typography>
        <Box>
          <Button
            variant={viewMode === 'form' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('form')}
            sx={{ mr: 1 }}
          >
            Submit New Grievance
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
          >
            View My Grievances ({grievances.length})
          </Button>
        </Box>
      </Box>

      {viewMode === 'form' ? (
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
            Submit New Grievance
          </Typography>
          
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* Employee Details Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                  Employee Details
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Would you prefer to fill this with your personal details or as anonymous
                </Typography>
                <RadioGroup
                  row
                  name="anonymous"
                  value={formik.values.anonymous.toString()}
                  onChange={(e) => formik.setFieldValue('anonymous', e.target.value === 'true')}
                >
                  <FormControlLabel value="false" control={<Radio />} label="Non-Anonymous" />
                  <FormControlLabel value="true" control={<Radio />} label="Anonymous" />
                </RadioGroup>
              </Grid>
              
              {!formik.values.anonymous && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="full_name"
                      label="Full Name"
                      value={formik.values.full_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                      helperText={formik.touched.full_name && formik.errors.full_name}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="employee_id"
                      label="Employee ID"
                      value={formik.values.employee_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.employee_id && Boolean(formik.errors.employee_id)}
                      helperText={formik.touched.employee_id && formik.errors.employee_id}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="department"
                      label="Department"
                      value={formik.values.department}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.department && Boolean(formik.errors.department)}
                      helperText={formik.touched.department && formik.errors.department}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="designation"
                      label="Designation"
                      value={formik.values.designation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.designation && Boolean(formik.errors.designation)}
                      helperText={formik.touched.designation && formik.errors.designation}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="contact_number"
                      label="Contact Number"
                      value={formik.values.contact_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.contact_number && Boolean(formik.errors.contact_number)}
                      helperText={formik.touched.contact_number && formik.errors.contact_number}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      required
                    />
                  </Grid>
                </>
              )}

                  {/* Grievance Details Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                      Grievance Details
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={formik.touched.nature_of_grievance && Boolean(formik.errors.nature_of_grievance)} required>
                      <InputLabel>Nature of Grievance</InputLabel>
                      <Select
                        name="nature_of_grievance"
                        value={formik.values.nature_of_grievance}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Nature of Grievance"
                      >
                        <MenuItem value="Workplace Harassment">Workplace Harassment</MenuItem>
                        <MenuItem value="Discrimination">Discrimination</MenuItem>
                        <MenuItem value="Managerial Misconduct">Managerial Misconduct</MenuItem>
                        <MenuItem value="Peer Conflict">Peer Conflict</MenuItem>
                        <MenuItem value="Policy-related issues">Policy-related issues</MenuItem>
                        <MenuItem value="Compensation/Benefits">Compensation/Benefits</MenuItem>
                        <MenuItem value="Work Environment">Work Environment</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    {formik.touched.nature_of_grievance && formik.errors.nature_of_grievance && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {formik.errors.nature_of_grievance}
                      </Typography>
                    )}
                  </Grid>
                  
                  {formik.values.nature_of_grievance === 'Other' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="grievance_other_detail"
                        label="Please Specify"
                        value={formik.values.grievance_other_detail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.grievance_other_detail && Boolean(formik.errors.grievance_other_detail)}
                        helperText={formik.touched.grievance_other_detail && formik.errors.grievance_other_detail}
                        required
                      />
                    </Grid>
                  )}
                  
                                     <Grid item xs={12} md={6}>
                     <TextField
                       fullWidth
                       name="date_of_incident"
                       label="Date of Incident"
                       type="date"
                       value={formik.values.date_of_incident}
                       onChange={formik.handleChange}
                       onBlur={formik.handleBlur}
                       error={formik.touched.date_of_incident && Boolean(formik.errors.date_of_incident)}
                       helperText={formik.touched.date_of_incident && formik.errors.date_of_incident}
                       InputLabelProps={{ shrink: true }}
                       required
                     />
                   </Grid>
                   
                   <Grid item xs={12} md={6}>
                     <TextField
                       fullWidth
                       name="reporting_date"
                       label="Reporting Date"
                       type="date"
                       value={formik.values.reporting_date}
                       onChange={formik.handleChange}
                       onBlur={formik.handleBlur}
                       error={formik.touched.reporting_date && Boolean(formik.errors.reporting_date)}
                       helperText={formik.touched.reporting_date && formik.errors.reporting_date}
                       InputLabelProps={{ shrink: true }}
                       required
                     />
                   </Grid>
                   
                   <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="location_of_incident"
                      label="Location of Incident"
                      value={formik.values.location_of_incident}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.location_of_incident && Boolean(formik.errors.location_of_incident)}
                      helperText={formik.touched.location_of_incident && formik.errors.location_of_incident}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="individuals_involved"
                      label="If any Individual or External Vendor is involved, please specify."
                      value={formik.values.individuals_involved}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="grievance_description"
                      label="Detailed Description of Grievance"
                      value={formik.values.grievance_description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.grievance_description && Boolean(formik.errors.grievance_description)}
                      helperText={formik.touched.grievance_description && formik.errors.grievance_description}
                      multiline
                      rows={4}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="steps_taken"
                      label="Steps Already Taken (if any)"
                      value={formik.values.steps_taken}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="preferred_resolution"
                      label="Preferred Resolution"
                      value={formik.values.preferred_resolution}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.preferred_resolution && Boolean(formik.errors.preferred_resolution)}
                      helperText={formik.touched.preferred_resolution && formik.errors.preferred_resolution}
                      multiline
                      rows={3}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 2
                    }}>
                      <Checkbox
                        name="declaration"
                        checked={formik.values.declaration}
                        onChange={(e) => formik.setFieldValue('declaration', e.target.checked)}
                        color="primary"
                        required
                        sx={{ 
                          mt: 0.5,
                          '& .MuiSvgIcon-root': {
                            fontSize: 20
                          },
                          transform: 'scale(1.2)',
                          transformOrigin: 'left center'
                        }}
                      />
                      <Typography variant="body1" sx={{ 
                        mt: 1, 
                        lineHeight: 1.7, 
                        flex: 1,
                        fontSize: '1.1rem',
                      }}>
                        I hereby declare that the information provided above is true to the best of my knowledge. I understand that false or misleading information may result in disciplinary action.
                      </Typography>
                    </Box>
                    {formik.touched.declaration && formik.errors.declaration && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 4 }}>
                        {formik.errors.declaration}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => setViewMode('list')}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={formik.isSubmitting}
                        sx={{ minWidth: 120 }}
                      >
                        {formik.isSubmitting ? <CircularProgress size={20} /> : 'Submit Grievance'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
        </Paper>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
            My Grievances
          </Typography>
          
          {grievances.length === 0 ? (
            <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="body1" color="text.secondary">
                No grievances submitted yet.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {grievances.map((grievance) => (
                <Grid item xs={12} key={grievance._id}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" color="primary">
                          Grievance #{grievance.grievanceId || grievance._id.slice(-6)}
                        </Typography>
                        <Chip
                          label={grievance.admin_section?.status || 'Pending'}
                          color={getStatusColor(grievance.admin_section?.status)}
                          size="small"
                        />
                      </Box>
                      
                                            <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Complainant:</strong> {grievance.anonymous ? 'Anonymous' : grievance.full_name || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Nature:</strong> {grievance.nature_of_grievance}
                            {grievance.grievance_other_detail && ` - ${grievance.grievance_other_detail}`}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Date of Incident:</strong> {formatDateLocal(grievance.date_of_incident)}
                          </Typography>
                        </Grid>
                        {grievance.reporting_date && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Reporting Date:</strong> {formatDateLocal(grievance.reporting_date)}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Description:</strong> {grievance.grievance_description}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Preferred Resolution:</strong> {grievance.preferred_resolution}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Submitted:</strong> {formatDateLocal(grievance.date_of_submission)}
                          </Typography>
                        </Grid>
                        
                        {/* Admin Actions and Updates */}
                        {grievance.actionTakenHistory && grievance.actionTakenHistory.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '18px' }}>
                              Action Taken History
                            </Typography>
                            <Box sx={{ 
                              maxHeight: '400px', 
                              overflowY: 'auto',
                              border: '1px solid',
                              borderColor: 'grey.200',
                              borderRadius: 2,
                              p: 2,
                              bgcolor: 'grey.50'
                            }}>
                              {grievance.actionTakenHistory.map((action, index) => (
                                <Box key={index} sx={{ mb: 3 }}>
                                  <Box sx={{ 
                                    p: 2, 
                                    bgcolor: action.deletedAt ? 'grey.50' : 'info.50', 
                                    borderRadius: 2, 
                                    border: '1px solid', 
                                    borderColor: action.deletedAt ? 'grey.300' : 'info.300',
                                    opacity: action.deletedAt ? 0.8 : 1,
                                    boxShadow: action.deletedAt ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip
                                          label={action.status}
                                          color={action.status === 'Resolved' ? 'success' : action.status === 'Escalated' ? 'error' : 'warning'}
                                          size="small"
                                          sx={{ fontWeight: 'bold' }}
                                        />
                                        {action.deletedAt && (
                                          <Chip
                                            label="Previous Update"
                                            color="default"
                                            size="small"
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                      <Typography variant="caption" sx={{ 
                                        color: 'text.secondary',
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem'
                                      }}>
                                        {formatDateTime(action.created_at)}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ 
                                      mt: 1,
                                      lineHeight: 1.6,
                                      color: 'text.primary'
                                    }}>
                                      {action.action_taken}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Grid>
                        )}
                        
                        {/* Current Action Taken (if no history) */}
                        {(!grievance.actionTakenHistory || grievance.actionTakenHistory.length === 0) && grievance.admin_section?.action_taken && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Action Taken:
                            </Typography>
                            <Typography variant="body2" sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                              {grievance.admin_section.action_taken}
                            </Typography>
                          </Grid>
                        )}
                        
                        {/* Admin Notes */}
                        {grievance.admin_section?.admin_notes && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Admin Notes:
                            </Typography>
                            <Typography variant="body2" sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                              {grievance.admin_section.admin_notes}
                            </Typography>
                          </Grid>
                        )}
                        
                        {/* Resolution Notes */}
                        {grievance.admin_section?.resolution_notes && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Resolution Notes:
                            </Typography>
                            <Typography variant="body2" sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                              {grievance.admin_section.resolution_notes}
                            </Typography>
                          </Grid>
                        )}
                        
                        {/* Closure Date */}
                        {grievance.admin_section?.closure_date && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Closure Date:
                            </Typography>
                            <Typography variant="body2" sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                              {formatDateLocal(grievance.admin_section.closure_date)}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
  </Box>
);
};

export default GrievanceFormTab; 